import { useMemo, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import {
  addStandardFormSchema,
  type AddStandardFormData,
} from '@/schema/template';
import { useToast } from '@/hooks/use-toast';
import { BilingualTextAreas } from '../add-standards/components/bilingual-text-areas';
import { cn } from '@/lib/utils';
import {
  useStandardQuery,
  useUpdateStandardMutation,
} from '@/hooks/queries/useTemplates';
import { useCanManageTemplates } from '@/hooks/usePermissions'; 

export default function EditStandardsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const canManage = useCanManageTemplates();

  // Redirect admin users who try to access this page manually
  useEffect(() => {
    if (!canManage) {
      toast({
        title: t('common.accessDenied', 'Access Denied'),
        description: t('common.noPermission', 'You do not have permission to access this page'),
        variant: 'destructive',
      });
      navigate(`/admin/templates/${id}`, { replace: true });
    }
  }, [canManage, navigate, id, toast, t]);

  // Get the standard ID from navigation state or URL
  const standardId = location.state?.standardId || location.state?.standard?.id;

  // Fetch standard details from API
  const { data: standardDetailData, isLoading: isLoadingStandard } =
    useStandardQuery(standardId, {
      enabled: !!standardId,
    });

  const form = useForm<AddStandardFormData>({
    resolver: zodResolver(addStandardFormSchema),
    defaultValues: {
      department: '',
      touchpoint: '',
      subtouchpoint: 'none',
      title: '',
      standardTextEnglish: '',
      standardTextArabic: '',
      answerType: 'YNNA',
      commentRequired: false,
    },
  });

  // Update mutation
  const updateStandardMutation = useUpdateStandardMutation(standardId, {
    onSuccessCallback: () => {
      navigate(`/admin/templates/${id}`);
    },
  });

  // Pre-fill form when standard data is available from API
  useEffect(() => {
    if (standardDetailData) {
      form.reset({
        department: String(standardDetailData.department_id),
        touchpoint: String(standardDetailData.touchpoint_id),
        subtouchpoint: standardDetailData.sub_touchpoint_id
          ? String(standardDetailData.sub_touchpoint_id)
          : 'none',
        title: String(standardDetailData.title_id),
        standardTextEnglish: standardDetailData.question_en || '',
        standardTextArabic: standardDetailData.question_ar || '',
        answerType: 'YNNA',
        commentRequired: false,
      });
    }
  }, [standardDetailData, form]);

  const handleBack = () => {
    navigate(`/admin/templates/${id}`);
  };

  const onSubmit = (data: AddStandardFormData) => {
    if (!standardId) {
      toast({
        title: 'Error',
        description: 'Standard ID is missing',
        variant: 'destructive',
      });
      return;
    }

    // Prepare update payload according to API spec
    const updatePayload = {
      question_en: data.standardTextEnglish,
      question_ar: data.standardTextArabic,
      department_id: Number(data.department),
      touchpoint_id: Number(data.touchpoint),
      sub_touchpoint_id:
        data.subtouchpoint === 'none' ? null : Number(data.subtouchpoint),
      title_id: Number(data.title),
    };

    updateStandardMutation.mutate(updatePayload);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div
        className={cn(
          'bg-primary text-primary-foreground p-3 sm:p-4 lg:p-6',
          isRTL && 'rtl'
        )}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-primary-foreground hover:bg-primary-foreground/10 self-start xs:self-auto"
              size="sm"
            >
              <ArrowLeft
                className={cn(
                  'w-3 h-3 sm:w-4 sm:h-4',
                  isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                )}
              />
              <span className="text-xs sm:text-sm">
                {t('templates.admin.addStandards.backToStandards')}
              </span>
            </Button>
            <h1
              className={cn(
                'text-lg sm:text-xl lg:text-2xl font-semibold truncate',
                isRTL && 'text-right'
              )}
            >
              <span className="hidden sm:inline">
                {t('templates.admin.editStandards.pageTitle')} -{' '}
                {standardDetailData?.property_type?.name || 'Standard'}
              </span>
              <span className="sm:hidden">{t('edit')} Standard</span>
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-2">
        <Card>
          <CardHeader className="p-3 sm:p-4 lg:p-6">
            <CardTitle
              className={cn(
                'flex flex-col xs:flex-row xs:items-center gap-2 text-base sm:text-lg',
                isRTL && 'text-right'
              )}
            >
              <div className="flex items-center gap-2">
                <Save className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">
                  {t('templates.admin.editStandards.title')}
                </span>
                <span className="sm:hidden">{t('edit')}</span>
              </div>
              {standardDetailData && (
                <span className="text-xs sm:text-sm font-normal text-muted-foreground">
                  (ID: {standardDetailData.id})
                </span>
              )}
            </CardTitle>
            {standardDetailData && (
              <div
                className={cn(
                  'text-xs sm:text-sm text-muted-foreground bg-muted/50 p-2 sm:p-3 rounded',
                  isRTL && 'text-right'
                )}
              >
                <strong>
                  {t('templates.admin.editStandards.currentStandard')}:
                </strong>
                <span
                  className={cn(
                    'block sm:inline mt-1 sm:mt-0',
                    isRTL ? 'mr-0 sm:mr-1' : 'ml-0 sm:ml-1'
                  )}
                >
                  {standardDetailData.question_en.substring(
                    0,
                    window.innerWidth < 640 ? 60 : 100
                  )}
                  {standardDetailData.question_en.length >
                  (window.innerWidth < 640 ? 60 : 100)
                    ? '...'
                    : ''}
                </span>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-3 sm:p-4 lg:p-6">
            {isLoadingStandard ? (
              <div className={cn('text-center py-8 sm:py-12', isRTL && 'rtl')}>
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Loading standard details...
                </p>
              </div>
            ) : !standardId || !standardDetailData ? (
              <div className={cn('text-center py-8 sm:py-12', isRTL && 'rtl')}>
                <div className="text-4xl sm:text-5xl lg:text-6xl mb-4">⚠️</div>
                <h3
                  className={cn(
                    'text-base sm:text-lg font-semibold mb-2',
                    isRTL && 'text-right'
                  )}
                >
                  {t('templates.admin.emptyState.title')}
                </h3>
                <p
                  className={cn(
                    'text-muted-foreground mb-4 sm:mb-6 max-w-md mx-auto text-sm sm:text-base px-4',
                    isRTL && 'text-right'
                  )}
                >
                  {t('templates.admin.emptyState.description')}
                </p>
                <Button
                  variant="outline"
                  onClick={handleBack}
                  size="sm"
                  className="text-xs sm:text-sm"
                >
                  <ArrowLeft
                    className={cn(
                      'w-3 h-3 sm:w-4 sm:h-4',
                      isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                    )}
                  />
                  {t('templates.admin.detail.backToTemplates')}
                </Button>
              </div>
            ) : (
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn('space-y-4 sm:space-y-6', isRTL && 'rtl')}
              >
                <div
                  className={cn(
                    'text-center py-2 sm:py-4',
                    isRTL && 'text-right'
                  )}
                >
                  <p
                    className={cn(
                      'text-gray-500 mb-3 sm:mb-6 text-sm sm:text-base px-4',
                      isRTL && 'text-right'
                    )}
                  >
                    {t('templates.admin.editStandards.description')}
                  </p>
                </div>

                {/* Note: Hierarchy editing is disabled - only allow editing questions */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-2 font-semibold">
                    Current Hierarchy:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <strong>Department:</strong>{' '}
                      {standardDetailData.department.name}
                    </div>
                    <div>
                      <strong>Touchpoint:</strong>{' '}
                      {standardDetailData.touchpoint.name}
                    </div>
                    <div>
                      <strong>Sub-Touchpoint:</strong>{' '}
                      {standardDetailData.sub_touchpoint?.name || 'None'}
                    </div>
                    <div>
                      <strong>Title:</strong> {standardDetailData.title.name}
                    </div>
                  </div>
                </div>

                {/* Standard Text Fields */}
                <BilingualTextAreas
                  control={form.control}
                  errors={form.formState.errors}
                />

                {/* Form Actions */}
                <div
                  className={cn(
                    'flex flex-col xs:flex-row items-stretch xs:items-center gap-2 sm:gap-4 pt-4 sm:pt-6 border-t',
                    isRTL && 'xs:flex-row-reverse'
                  )}
                >
                  <Button
                    type="submit"
                    size="sm"
                    className="w-full xs:w-auto text-xs sm:text-sm order-1"
                    disabled={updateStandardMutation.isPending}
                  >
                    {updateStandardMutation.isPending ? (
                      <>
                        <Loader2
                          className={cn(
                            'w-3 h-3 sm:w-4 sm:h-4 animate-spin',
                            isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                          )}
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save
                          className={cn(
                            'w-3 h-3 sm:w-4 sm:h-4',
                            isRTL ? 'ml-1 sm:ml-2' : 'mr-1 sm:mr-2'
                          )}
                        />
                        {t('templates.admin.editStandards.saveChanges')}
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleBack}
                    size="sm"
                    className="w-full xs:w-auto text-xs sm:text-sm order-3"
                    disabled={updateStandardMutation.isPending}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
